import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { FiSend, FiLoader } from "react-icons/fi";
import { API_URL } from "../creds";
import Header from "./Header";

function useAutoResize(textarea) {
  useEffect(() => {
    if (!textarea) return;

    const resize = () => {
      textarea.style.height = "auto";
      textarea.style.height =
        Math.min(textarea.scrollHeight, 160) + "px";
    };

    resize();

    textarea.addEventListener("input", resize);

    return () => textarea.removeEventListener("input", resize);
  }, [textarea]);
}

export default function UnitechHBB({isAuthenticated, handleLogout}) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const chatRef = useRef(null);
  const textareaRef = useRef(null);

  useAutoResize(textareaRef.current);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isLoading]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    const question = inputValue.trim();

    if (!question || isLoading) return;

    // Show user prompt immediately
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        role: "user",
        content: question,
      },
    ]);

    setInputValue("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question,
          // verbose: false,
          projectid: "hearyourself"
        }),
      });

      const data = await response.json();

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "assistant",
          content: data.answer || "No relevant information found in the private RAG.",
        },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "assistant",
          content: "Network error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Header isAuthenticated={isAuthenticated} handleLogout={handleLogout} />
      {/* Chat Area */}
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto px-6 py-6 pb-40"
      >
        {messages.length === 0 && !isLoading ? (
          <div className="h-full flex flex-col justify-center items-center text-center">
            <h1 className="text-4xl font-normal mb-3">
              Unitech HBB
            </h1>

            <p className="text-gray-500 max-w-md">
              Ask questions from your private RAG knowledge base.
            </p>
          </div>
        ) : (
          <div className="space-y-6 max-w-5xl mx-auto">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end"  : "justify-start"}`}>
                <div className={` max-w-[85%] rounded-2xl px-5 py-4 shadow-sm border whitespace-pre-wrap ${msg.role === "user" ? "bg-green-500/20 border-blue-700/10" : "bg-white border-gray-300"}`}>
                  {msg.role === "assistant" ? (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {/* Loading Spinner */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <FiLoader
                      className="animate-spin text-blue-600"
                      size={32}
                    />

                    <span className="text-gray-500 text-sm">
                      Searching, Please Wait...
                    </span>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-2">
        <div className="max-w-5xl mx-auto relative">

          <textarea
            ref={textareaRef}
            value={inputValue}
            disabled={isLoading}
            rows={1}
            placeholder="Ask anything...."
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full resize-none rounded-3xl border border-gray-300 px-5 py-4 pr-16 focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-40  overflow-y-auto bg-white"
          />

          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            title="response button"
            className="absolute right-3 bottom-3 bg-blue-600 hover:bg-blue-700
              disabled:bg-gray-400 text-white rounded-full p-3 transition-all"
          >
            <FiSend size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}